import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { useSidebar } from '@/contexts/SidebarContext';

interface TourProps {
  active: boolean;
  onClose: () => void;
  onPanelChange?: (key: string) => void;
}

interface TourStep {
  selector: string;
  title: string;
  description: string;
  /** Optional panel key the parent should switch to before this step runs */
  panelKey?: string;
}

const STEPS: TourStep[] = [
  {
    selector: '[data-tour="sidebar-dashboard"]',
    title: 'Dashboard',
    description:
      'Your trading hub. Live pulse cards for the 4 pairs you pin, AI-driven bias on each, plus risk and currency-strength views.',
    panelKey: 'dashboard',
  },
  {
    selector: '[data-tour="sidebar-news"]',
    title: 'News Feed',
    description:
      'Live financial news plus economic-event outcomes. Tap any item for an AI take with concrete trade ideas.',
  },
  {
    selector: '[data-tour="sidebar-bookmarks"]',
    title: 'Bookmarks',
    description:
      'Save any news story for later — handy when something hits during a trade and you want to revisit. Export your saved list any time.',
  },
  {
    selector: '[data-tour="sidebar-calendar"]',
    title: 'Economic Calendar',
    description:
      'Every scheduled release with forecast / actual / previous and a per-event AI playbook covering bullish / neutral / bearish scenarios.',
  },
  {
    selector: '[data-tour="sidebar-backtest"]',
    title: 'Backtest',
    description:
      'Replay any major historical news event on a real chart — see how price actually reacted, with the 12 macro indicators that were live at that moment alongside.',
  },
  {
    selector: '[data-tour="sidebar-journal"]',
    title: 'Trading Journal (Elite)',
    description:
      "Connect MT5 once and every closed trade auto-syncs into TELOS. Set goals, track your risk reality vs intent, and see where your edge actually lives. Elite only.",
  },
  {
    selector: '[data-tour="sidebar-economy"]',
    title: 'Economy',
    description:
      'Macro indicators and central-bank rates at a glance. Quick read on the global backdrop.',
  },
  {
    selector: '[data-tour="sidebar-cot"]',
    title: 'COT Report',
    description:
      'Weekly Commitments-of-Traders positioning data. See how the smart money is leaning.',
  },
  {
    selector: '[data-tour="sidebar-research"]',
    title: 'Stock Research',
    description:
      'Daily Stock of the Day picked from live analyst signals, plus search by ticker for fundamentals, charts and AI rationale.',
  },
  {
    selector: '[data-tour="sidebar-lotcalc"]',
    title: 'Lot Calculator',
    description:
      'Position-sizing tool. Type your account size, risk %, stop distance — get the exact lot that keeps risk per trade in check.',
  },
  {
    selector: '[data-tour="sidebar-tutorials"]',
    title: 'New to Trading',
    description:
      'Step-by-step walkthroughs of MT5 (entering trades, modifying SL/TP, charting essentials).',
  },
  {
    selector: '[data-tour="sidebar-affiliate"]',
    title: 'Affiliate',
    description:
      'Refer traders to TELOS and earn recurring commission on every Pro subscription you bring in.',
  },
  {
    selector: '[data-tour="dashboard-customise"]',
    title: 'Customise pairs',
    description:
      'Tap Customise to pick the four pairs you want pinned on your dashboard. Change them any time.',
    panelKey: 'dashboard',
  },
  {
    selector: '[data-tour="sidebar-profile"]',
    title: 'Profile',
    description:
      'Tap your avatar at the bottom-left for account settings, to manage your plan, or to replay this tour.',
  },
];

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const RING_PADDING = 6;
const POPOVER_WIDTH = 320;
const POPOVER_OFFSET = 16;
const VIEWPORT_MARGIN = 12;

function readRect(el: Element): Rect {
  const r = el.getBoundingClientRect();
  return {
    top: r.top - RING_PADDING,
    left: r.left - RING_PADDING,
    width: r.width + RING_PADDING * 2,
    height: r.height + RING_PADDING * 2,
  };
}

type PopoverPlacement = 'below' | 'above' | 'right' | 'left';

interface PopoverPos {
  top: number;
  left: number;
  placement: PopoverPlacement;
}

function computePopoverPos(ring: Rect, popoverHeight: number): PopoverPos {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Try below
  const belowTop = ring.top + ring.height + POPOVER_OFFSET;
  if (belowTop + popoverHeight + VIEWPORT_MARGIN <= vh) {
    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, ring.left + ring.width / 2 - POPOVER_WIDTH / 2),
      vw - POPOVER_WIDTH - VIEWPORT_MARGIN,
    );
    return { top: belowTop, left, placement: 'below' };
  }

  // Try above
  const aboveTop = ring.top - POPOVER_OFFSET - popoverHeight;
  if (aboveTop >= VIEWPORT_MARGIN) {
    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, ring.left + ring.width / 2 - POPOVER_WIDTH / 2),
      vw - POPOVER_WIDTH - VIEWPORT_MARGIN,
    );
    return { top: aboveTop, left, placement: 'above' };
  }

  // Try right
  const rightLeft = ring.left + ring.width + POPOVER_OFFSET;
  if (rightLeft + POPOVER_WIDTH + VIEWPORT_MARGIN <= vw) {
    const top = Math.min(
      Math.max(VIEWPORT_MARGIN, ring.top + ring.height / 2 - popoverHeight / 2),
      vh - popoverHeight - VIEWPORT_MARGIN,
    );
    return { top, left: rightLeft, placement: 'right' };
  }

  // Fallback: left
  const leftLeft = ring.left - POPOVER_OFFSET - POPOVER_WIDTH;
  const top = Math.min(
    Math.max(VIEWPORT_MARGIN, ring.top + ring.height / 2 - popoverHeight / 2),
    vh - popoverHeight - VIEWPORT_MARGIN,
  );
  return { top, left: Math.max(VIEWPORT_MARGIN, leftLeft), placement: 'left' };
}

export function Tour({ active, onClose, onPanelChange }: TourProps) {
  const [index, setIndex] = useState(0);
  const [ring, setRing] = useState<Rect | null>(null);
  const [popover, setPopover] = useState<PopoverPos | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  // forceClosed is a local hard-kill flag — set the instant finish() runs so
  // the tour disappears immediately, regardless of whether the parent's
  // active prop has flipped to false yet. This is the actual fix for the
  // 'tour stays on screen after Done' bug: we no longer depend on parent
  // state propagation OR on AnimatePresence exit animations.
  const [forceClosed, setForceClosed] = useState(false);
  // Ref parallels the state so the render gate can consult both — even if a
  // setForceClosed call somehow gets dropped (stale closure, batching edge),
  // the ref still flips synchronously and the next render kills the tour.
  const forceClosedRef = useRef(false);

  // Reset to first step whenever the tour is activated. Also clear the
  // forceClosed hard-kill flag (both state AND ref) so a replay actually
  // re-renders the overlay.
  useEffect(() => {
    if (active) {
      setIndex(0);
      setForceClosed(false);
      forceClosedRef.current = false;
    }
  }, [active]);

  // Lock body scroll while tour is active.
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [active]);

  // Pin the sidebar open during the tour — otherwise the collapsed sidebar
  // shows icons only and the highlighted page names aren't visible. Restore
  // the user's prior pin state when the tour ends, and nudge a re-measure
  // once the expand animation has settled so the spotlight ring stays glued.
  const { pinned, setPinned } = useSidebar();
  useEffect(() => {
    if (!active) return;
    const wasPinned = pinned;
    setPinned(true);
    const t = window.setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
    return () => { window.clearTimeout(t); setPinned(wasPinned); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Emergency window-level kill — call window.__TELOS_KILL_TOUR__() from
  // DevTools if the tour ever gets stuck again. Bypasses every state path.
  useEffect(() => {
    (window as unknown as { __TELOS_KILL_TOUR__?: () => void }).__TELOS_KILL_TOUR__ = () => {
      forceClosedRef.current = true;
      setForceClosed(true);
      try { document.body.style.overflow = ''; } catch { /* ignore */ }
      try { onClose(); } catch { /* ignore */ }
      // eslint-disable-next-line no-console
      console.warn('[Tour] emergency kill via window.__TELOS_KILL_TOUR__');
    };
    return () => {
      delete (window as unknown as { __TELOS_KILL_TOUR__?: () => void }).__TELOS_KILL_TOUR__;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ESC to close.
  useEffect(() => {
    if (!active) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        finish();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const currentStep = STEPS[index];

  // Request a panel switch when the step demands it.
  useEffect(() => {
    if (!active || !currentStep) return;
    if (currentStep.panelKey && onPanelChange) {
      onPanelChange(currentStep.panelKey);
    }
  }, [active, currentStep, onPanelChange]);

  // Locate the target element and (re)position the ring + popover.
  // Retries briefly to wait for animated targets (panel transitions, modals).
  // After ~1s of failure we give up and skip the step — this prevents the
  // tour from getting stuck on a dimmed screen with only Exit visible.
  useLayoutEffect(() => {
    if (!active || !currentStep) return;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 13; // 13 * 75ms ≈ 1s

    // Clear stale ring/popover BEFORE retrying so the user sees the dim
    // backdrop transitioning rather than an old ring stuck at a wrong spot.
    setRing(null);
    setPopover(null);

    function tryLocate() {
      if (cancelled) return;
      const el = document.querySelector(currentStep.selector);
      if (!el) {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          // Target never materialised — skip this step (or finish if last).
          advance(true);
          return;
        }
        window.setTimeout(tryLocate, 75);
        return;
      }
      const rect = readRect(el);
      setRing(rect);
      const popoverHeight = popoverRef.current?.offsetHeight ?? 180;
      setPopover(computePopoverPos(rect, popoverHeight));
    }

    tryLocate();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, index]);

  // Reposition on resize / scroll so the ring stays glued to its target.
  useEffect(() => {
    if (!active || !currentStep) return;
    function reposition() {
      const el = document.querySelector(currentStep.selector);
      if (!el) return;
      const rect = readRect(el);
      setRing(rect);
      const popoverHeight = popoverRef.current?.offsetHeight ?? 180;
      setPopover(computePopoverPos(rect, popoverHeight));
    }
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [active, currentStep]);

  function finish() {
    // eslint-disable-next-line no-console
    console.warn('[Tour] finish() called — closing tour now.');
    // Ref flips synchronously (no React batching) — render gate reads this.
    forceClosedRef.current = true;
    // State flip causes a re-render so the next paint reflects the close.
    setForceClosed(true);
    // Restore body scroll directly in case the lock-cleanup useEffect deps
    // don't trigger fast enough.
    try { document.body.style.overflow = ''; } catch { /* ignore */ }
    // Bubble up so parent (TourContext) can clear isActive + persist
    // tour-completed flag. If this throws or no-ops, we still rendered null.
    try { onClose(); } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Tour] onClose threw:', e);
    }
  }

  function advance(silent = false) {
    if (index >= STEPS.length - 1) {
      finish();
      return;
    }
    setIndex((i) => i + 1);
    if (!silent) {
      // visual reset between transitions to avoid the ring lingering at old position
      setPopover(null);
    }
  }

  function back() {
    if (index <= 0) return;
    setIndex((i) => i - 1);
  }

  const isLast = index === STEPS.length - 1;
  // Belt-and-braces: if index ever exceeds the last step (defensive — should
  // not happen, but guards against the tour getting stuck visible if any
  // future change calls setIndex past the end without calling finish()).
  useEffect(() => {
    if (active && index >= STEPS.length) finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, index]);

  // When the user reaches the LAST step we give them ~3 s to read it, then
  // auto-finish — no more "stuck on the last frame waiting to click Done".
  // The Done button still works for impatient users and Exit tour is always
  // available as the safety hatch.
  useEffect(() => {
    if (!active || !isLast) return;
    const t = window.setTimeout(() => finish(), 800);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, isLast]);

  // Hard ceiling: tour can never stay open for more than 45 seconds. If the
  // user reaches this point they were stuck (bad target, missing panel, etc.)
  // and the safety-net Exit X is the only escape. Force-close from here so
  // there is ALWAYS a guaranteed exit even if every other path is broken.
  useEffect(() => {
    if (!active) return;
    const t = window.setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn('[Tour] 45s safety timeout fired — force-closing tour.');
      finish();
    }, 45000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // If we are stuck on a single step for more than 6 seconds with no popover
  // ever rendering, force-skip. This is the actual fix for the "Exit X is the
  // only thing visible" failure mode where the target is found enough to not
  // bail in tryLocate but the popover never appears (off-viewport, height=0,
  // etc.).
  useEffect(() => {
    if (!active) return;
    const t = window.setTimeout(() => {
      if (!popover) {
        // eslint-disable-next-line no-console
        console.warn('[Tour] Step', index, 'never rendered popover — skipping.');
        advance(true);
      }
    }, 6000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, index, popover]);
  const isFirst = index === 0;

  const portalTarget = useMemo(
    () => (typeof document !== 'undefined' ? document.body : null),
    [],
  );

  if (!portalTarget) return null;

  // Belt-and-braces hard kill: if forceClosed flipped, return null
  // unconditionally — no AnimatePresence, no exit animation, no chance of
  // lingering. This is the actual fix for "Done leaves tour visible".
  if (forceClosedRef.current || forceClosed || !active || !currentStep) return null;

  return createPortal(
    <div
      key="tour-root"
      className="fixed inset-0 z-[100]"
      aria-modal="true"
      role="dialog"
      aria-label="Product tour"
    >
          {/* Backdrop absorbs clicks (no dismiss-on-click). When a spotlight
              ring is active, the ring's huge box-shadow already dims the area
              AROUND it — so we keep this layer transparent so it doesn't
              re-dim the spotlight itself, which was making the spotlighted
              page name unreadable. */}
          <div
            className={cn('absolute inset-0', !ring && 'bg-bg/65')}
            onClick={(e) => e.stopPropagation()}
          />

          {/* Always-on Exit button. The popover normally provides Skip / Done
              / X, but if it ever fails to render (target element missing,
              layout race, etc.) the user was previously stuck on a dimmed
              screen with no escape. This guarantees a way out. */}
          <button
            type="button"
            onClick={finish}
            aria-label="Exit tour"
            className={cn(
              'absolute top-4 right-4 z-[102]',
              'inline-flex items-center gap-1.5 h-8 px-3 rounded-full',
              'bg-bg-elevated border border-border-strong text-fg text-2xs font-semibold',
              'shadow-elevation hover:bg-bg-card transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
            )}
          >
            <X className="size-3.5" />
            Exit tour
          </button>

          {/* Spotlight ring — overlays a cutout via huge box-shadow. */}
          {ring && (
            <motion.div
              key="tour-ring"
              initial={false}
              animate={{
                top: ring.top,
                left: ring.left,
                width: ring.width,
                height: ring.height,
                opacity: 1,
              }}
              transition={{ type: 'spring', stiffness: 260, damping: 30, mass: 0.7 }}
              className={cn(
                'pointer-events-none absolute rounded-xl',
                'border-2 border-accent',
              )}
              style={{
                boxShadow: '0 0 0 9999px rgb(var(--bg) / 0.65)',
              }}
            />
          )}

          {/* Popover */}
          {popover && (
            <motion.div
              key={`tour-pop-${index}`}
              ref={popoverRef}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              style={{
                top: popover.top,
                left: popover.left,
                width: POPOVER_WIDTH,
              }}
              className={cn(
                'absolute z-[101]',
                'bg-bg-elevated border border-border-strong rounded-xl shadow-elevation',
                'p-4 space-y-3',
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-2xs uppercase tracking-wider text-accent font-semibold">
                    Step {index + 1} / {STEPS.length}
                  </div>
                  <h3 className="text-base font-bold text-fg mt-1 leading-tight">
                    {currentStep.title}
                  </h3>
                </div>
                <button
                  onClick={finish}
                  className="p-1 -m-1 rounded-md text-fg-subtle hover:text-fg hover:bg-bg-card transition-colors"
                  aria-label="Close tour"
                >
                  <X className="size-4" />
                </button>
              </div>

              <p className="text-sm text-fg-muted leading-relaxed">
                {currentStep.description}
              </p>

              {/* Step progress dots */}
              <div className="flex items-center gap-1 pt-1">
                {STEPS.map((_, i) => (
                  <span
                    key={i}
                    className={cn(
                      'h-1 rounded-full transition-all duration-300',
                      i === index ? 'w-4 bg-accent' : 'w-1.5 bg-border',
                    )}
                  />
                ))}
              </div>

              <div className="flex items-center justify-between pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={finish}
                  className="text-fg-subtle hover:text-fg"
                >
                  Skip tour
                </Button>
                <div className="flex items-center gap-2">
                  {!isFirst && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={back}
                      aria-label="Previous step"
                    >
                      <ChevronLeft className="size-3.5" />
                      Back
                    </Button>
                  )}
                  {isLast ? (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        // Direct dual-write: don't trust finish() to be the
                        // version captured in this render's closure. Flip
                        // forceClosed AND bubble onClose simultaneously so
                        // the tour goes away on the very next render.
                        setForceClosed(true);
                        onClose();
                      }}
                    >
                      Done
                    </Button>
                  ) : (
                    <Button variant="primary" size="sm" onClick={() => advance()}>
                      Next
                      <ChevronRight className="size-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
    </div>,
    portalTarget,
  );
}
